import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Hash, Loader2, Package, Phone, Search, ShoppingBag, User,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { lookupOrders } from '../api/orders';
import { listProducts } from '../api/inventory';
import StatusBadge from '../components/StatusBadge';
import { formatCurrency } from '../data/mockData';
import { formatPrice } from '../utils/soldBy';
import styles from './Topbar.module.css';

const DEBOUNCE_MS = 280;
const MIN_QUERY = 2;

function digitsOnly(value) {
  return (value || '').replace(/\D/g, '');
}

function isPhoneQuery(q) {
  const trimmed = (q || '').trim();
  // Hex/alphanumeric order IDs (e.g. f3d350f7…) contain letters — never treat as phone,
  // even though stripping letters can leave 10+ digits.
  if (/[a-z]/i.test(trimmed)) return false;
  const digits = digitsOnly(trimmed);
  return digits.length >= 10 && digits.length <= 15;
}

function orderLookupParams(q) {
  const trimmed = (q || '').trim();
  if (isPhoneQuery(trimmed)) {
    return { phone: digitsOnly(trimmed).slice(-10) };
  }
  return { orderId: trimmed };
}

export default function GlobalSearch() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const reqIdRef = useRef(0);

  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [customerPhone, setCustomerPhone] = useState(null);
  const [error, setError] = useState('');

  const close = useCallback(() => {
    setOpen(false);
  }, []);

  const clearResults = useCallback(() => {
    setOrders([]);
    setProducts([]);
    setCustomerPhone(null);
    setError('');
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [close]);

  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) close();
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [close]);

  useEffect(() => {
    const q = query.trim();
    if (q.length < MIN_QUERY) {
      clearResults();
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    setError('');
    const timer = setTimeout(async () => {
      const reqId = ++reqIdRef.current;
      try {
        const params = orderLookupParams(q);
        const [orderResult, productResult] = await Promise.all([
          lookupOrders(params, user.idToken).catch(() => ({ orders: [], phone: null })),
          listProducts(user.idToken, { q, limit: 6 }).catch(() => ({ items: [] })),
        ]);
        if (reqId !== reqIdRef.current) return;
        setOrders((orderResult.orders || []).slice(0, 6));
        setCustomerPhone(orderResult.phone || (isPhoneQuery(q) ? params.phone : null));
        setProducts((productResult.items || []).slice(0, 6));
      } catch (err) {
        if (reqId !== reqIdRef.current) return;
        clearResults();
        setError(err.message || 'Search failed.');
      } finally {
        if (reqId === reqIdRef.current) setLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [query, user.idToken, clearResults]);

  const goOrder = (order) => {
    close();
    setQuery('');
    clearResults();
    navigate(`/order-lookup?orderId=${encodeURIComponent(order.id)}`, {
      state: {
        orderId: order.id,
        seedOrders: [order],
      },
    });
  };

  const goOrdersByPhone = (phone) => {
    const normalized = digitsOnly(phone).slice(-10) || phone;
    close();
    setQuery('');
    clearResults();
    navigate(`/order-lookup?phone=${encodeURIComponent(normalized)}`, {
      state: {
        phone: normalized,
        seedOrders: orders,
      },
    });
  };

  const goProduct = (productQuery) => {
    close();
    setQuery('');
    clearResults();
    navigate(`/products?q=${encodeURIComponent(productQuery)}`);
  };

  const showDropdown = open && query.trim().length >= MIN_QUERY;
  const hasResults = orders.length > 0 || products.length > 0 || customerPhone;

  return (
    <div className={styles.searchWrap} ref={wrapRef}>
      <Search size={15} className={styles.searchIcon} />
      <input
        ref={inputRef}
        className={styles.search}
        placeholder="Search orders, products, customers…"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        role="combobox"
        aria-label="Global search"
        aria-expanded={showDropdown}
        aria-controls="global-search-results"
        aria-autocomplete="list"
        autoComplete="off"
      />

      {showDropdown && (
        <div id="global-search-results" className={styles.searchDropdown} role="listbox">
          {loading && (
            <div className={styles.searchStatus}>
              <Loader2 size={14} className={styles.searchSpin} /> Searching…
            </div>
          )}

          {!loading && error && (
            <div className={styles.searchStatus}>{error}</div>
          )}

          {!loading && !error && !hasResults && (
            <div className={styles.searchStatus}>No matches for “{query.trim()}”</div>
          )}

          {!loading && !error && customerPhone && (
            <div className={styles.searchSection}>
              <p className={styles.searchSectionLabel}>
                <User size={12} /> Customer
              </p>
              <button
                type="button"
                className={styles.searchHit}
                onClick={() => goOrdersByPhone(customerPhone)}
              >
                <span className={styles.searchHitIcon}><Phone size={15} /></span>
                <span className={styles.searchHitBody}>
                  <span className={styles.searchHitTitle}>{customerPhone}</span>
                  <span className={styles.searchHitMeta}>
                    {orders.length} order{orders.length === 1 ? '' : 's'} · Open in Order Lookup
                  </span>
                </span>
              </button>
            </div>
          )}

          {!loading && !error && orders.length > 0 && (
            <div className={styles.searchSection}>
              <p className={styles.searchSectionLabel}>
                <ShoppingBag size={12} /> Orders
              </p>
              {orders.map((order) => (
                  <button
                    type="button"
                    key={order.id}
                    className={styles.searchHit}
                    onClick={() => goOrder(order)}
                  >
                    <span className={styles.searchHitIcon}><Hash size={15} /></span>
                    <span className={styles.searchHitBody}>
                      <span className={styles.searchHitTitle}>{order.id}</span>
                      <span className={styles.searchHitMeta}>
                        {order.customer?.phone || order.address || 'Order'}
                        {' · '}
                        {formatCurrency(order.total)}
                      </span>
                    </span>
                    <StatusBadge status={order.status} size="sm" />
                  </button>
              ))}
            </div>
          )}

          {!loading && !error && products.length > 0 && (
            <div className={styles.searchSection}>
              <p className={styles.searchSectionLabel}>
                <Package size={12} /> Products
              </p>
              {products.map((product) => (
                <button
                  type="button"
                  key={product.id}
                  className={styles.searchHit}
                  onClick={() => goProduct(product.sku || product.name)}
                >
                  <span className={styles.searchHitIcon}><Package size={15} /></span>
                  <span className={styles.searchHitBody}>
                    <span className={styles.searchHitTitle}>{product.name}</span>
                    <span className={styles.searchHitMeta}>
                      {product.sku ? `SKU ${product.sku} · ` : ''}
                      {formatPrice(product, formatCurrency)}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
